"use client";

import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Globe } from "lucide-react";
import { locales } from "@/i18n/settings";
import { useAuthStore } from "@/store/zustand/useAuthStore";
import { axiosInstance } from "@/lib/axios";
import { updateCurrentUser } from "@membentry/shared-api";
import { toast } from "sonner";

export function LanguageSwitcher() {
  const locale = useLocale();
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);

  const handleLanguageChange = (newLocale: string) => {
    const segments = pathname.split("/");
    segments[1] = newLocale;
    const newPath = segments.join("/");

    if (isLoggedIn) {
      void updateCurrentUser(axiosInstance, { language: newLocale, languageSetByUser: true }).catch(() => {
        toast.error("Could not save language preference");
      });
    }
    router.push(newPath);
    router.refresh();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="rounded-xl bg-background/70">
          <Globe className="w-5 h-5" />
          <span className="sr-only">{t("locale.switchLanguage")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl">
        {locales.map((loc) => (
          <DropdownMenuItem
            key={loc}
            onClick={() => handleLanguageChange(loc)}
            className={loc === locale ? "bg-muted font-medium" : ""}
          >
            {loc === "en" ? t("locale.english") : t("locale.serbian")}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
